import React, { Fragment, Suspense } from 'react';
import { useIntl } from 'react-intl-next';
import { IconButton } from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import Blog16Icon from '@atlaskit/icon-object/glyph/blog/16';
import Page16Icon from '@atlaskit/icon-object/glyph/page/16';
import OpenFolderIcon from '@atlaskit/icon/core/folder-open';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import FileIcon from '@atlaskit/icon/core/migration/file';
import OfficeBuildingIcon from '@atlaskit/icon/core/migration/office-building';
import DocumentsIcon from '@atlaskit/icon/core/migration/pages--documents';
import QuoteIcon from '@atlaskit/icon/core/migration/quotation-mark--quote';
import { OpenFolderIcon as LegacyOpenFolderIcon } from '@atlaskit/legacy-custom-icons';
import Link from '@atlaskit/link';
import Modal, { ModalBody, ModalFooter, ModalTitle } from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import TimeAgo from '@atlassian/mpt-timeago';
import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { EditedDefinitionScope } from '../types';
import { messages } from './messages';
import { useDefinitionEditHistory } from './useDefinitionEditHistory';
var MarkdownRenderer = /*#__PURE__*/React.lazy(function () {
  return import( /* webpackChunkName: "react-markdown" */'react-markdown').then(function (_ref) {
    var ReactMarkdown = _ref.default;
    return {
      default: ReactMarkdown
    };
  });
});
var loadingSpinnerStyles = xcss({
  display: 'flex',
  justifyContent: 'center',
  height: '100%',
  width: '100%'
});
var editHistoryModalStyles = xcss({
  display: 'flex',
  paddingTop: 'space.300',
  paddingLeft: 'space.300',
  paddingRight: 'space.300',
  paddingBottom: 'space.100',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative'
});
var editHistoryWrapperStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: 'space.250',
  gap: 'space.200',
  font: "var(--ds-font-body, normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)"
});
var editHistoryDescriptionStyles = xcss({
  color: 'color.text',
  gap: 'space.050'
});
var editedDefinitionCardContainerStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space.100'
});
var editedDefinitionScopeTextStyles = xcss({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  color: 'color.text.subtlest'
});
var editedDefinitionCardStyles = xcss({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  padding: 'space.200',
  borderRadius: "var(--ds-space-100, 8px)",
  border: "1px solid ".concat("var(--ds-border, #091E4224)"),
  gap: 'space.200'
});
var editedDefinitionAvatarStyles = xcss({
  borderRadius: '64%'
});
var editedDefinitionCardBodyStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  color: 'color.text',
  font: "var(--ds-font-body, normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  gap: 'space.150'
});
var editedDefinitionCardHeaderWrapperStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space.0'
});
var editedDefinitionCardHeaderSpacerStyles = {
  width: '100%',
  color: "var(--ds-border, #F0F0F3)",
  marginTop: 3,
  marginBottom: 0
};
var editedDefinitionCardHeaderStyles = xcss({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 'space.050',
  color: 'color.text.subtle',
  marginBottom: 'space.100'
});
var editedDefinitionCardEditorStyles = xcss({
  fontWeight: "var(--ds-font-weight-semibold, 600)",
  color: 'color.text'
});
var editedDefinitionCardReferenceStyles = xcss({
  display: 'flex',
  alignItems: 'center'
});
var editedDefinitionCardReferenceTitleStyles = xcss({
  marginLeft: 'space.050'
});
var ScopeDescription = function ScopeDescription(_ref2) {
  var scope = _ref2.scope;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var scopeDescription = '';
  var ScopeIcon;
  switch (scope) {
    case EditedDefinitionScope.ORGANIZATION:
      scopeDescription = formatMessage(messages.readingAidsEditHistoryVisibilityTextOrg);
      ScopeIcon = OfficeBuildingIcon;
      break;
    case EditedDefinitionScope.SPACE:
      scopeDescription = formatMessage(messages.readingAidsEditHistoryVisibilitySpace);
      ScopeIcon = DocumentsIcon;
      break;
    case EditedDefinitionScope.PAGE:
      scopeDescription = formatMessage(messages.readingAidsEditHistoryVisibilityPage);
      ScopeIcon = FileIcon;
      break;
    case EditedDefinitionScope.BLOGPOST:
      scopeDescription = formatMessage(messages.readingAidsEditHistoryVisibilityBlog);
      ScopeIcon = QuoteIcon;
      break;
    case EditedDefinitionScope.PROJECT:
      scopeDescription = formatMessage(messages.readingAidsEditHistoryVisibilityProject);
      ScopeIcon = function ScopeIcon() {
        return /*#__PURE__*/React.createElement(OpenFolderIcon, {
          color: "var(--ds-icon, #44546F)",
          label: "",
          LEGACY_fallbackIcon: LegacyOpenFolderIcon,
          LEGACY_size: "medium",
          LEGACY_margin: "0 0 0 ".concat("var(--ds-space-negative-050, -4px)")
        });
      };
      break;
    default:
      return null;
  }
  return /*#__PURE__*/React.createElement(Inline, {
    xcss: editedDefinitionScopeTextStyles,
    space: "space.050"
  }, scopeDescription, " ", /*#__PURE__*/React.createElement(ScopeIcon, {
    label: ""
  }));
};
export var DefinitionEditHistory = function DefinitionEditHistory(_ref3) {
  var curatedDefinitionScopeConfig = _ref3.curatedDefinitionScopeConfig,
    onClose = _ref3.onClose;
  var _useIntl2 = useIntl(),
    formatMessage = _useIntl2.formatMessage;
  var _useAIAnswerContext = useAIAnswerContext(),
    query = _useAIAnswerContext.commonAttributes.query;
  var _useDefinitionEditHis = useDefinitionEditHistory({
      query: query,
      curatedDefinitionScopeConfig: curatedDefinitionScopeConfig
    }),
    definitions = _useDefinitionEditHis.definitions,
    loading = _useDefinitionEditHis.loading;
  var hasError = definitions.length === 0 && !loading;
  return /*#__PURE__*/React.createElement(Modal, {
    onClose: onClose,
    height: 600
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: editHistoryModalStyles
  }, /*#__PURE__*/React.createElement(ModalTitle, null, formatMessage(messages.readingAidsEditHistoryHeader)), /*#__PURE__*/React.createElement(IconButton, {
    appearance: "subtle",
    icon: CrossIcon,
    label: "",
    onClick: onClose
  })), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(Stack, {
    xcss: editHistoryWrapperStyles
  }, /*#__PURE__*/React.createElement(Inline, {
    xcss: editHistoryDescriptionStyles
  }, formatMessage(messages.readingAidsEditHistoryDescription, {
    queryText: /*#__PURE__*/React.createElement("b", null, query)
  })), hasError || loading ? /*#__PURE__*/React.createElement(Box, {
    xcss: loadingSpinnerStyles
  }, loading && /*#__PURE__*/React.createElement(Spinner, {
    size: "large",
    testId: "definition-history-loading-spinner"
  }), hasError && /*#__PURE__*/React.createElement(EmptyState, {
    header: formatMessage(messages.readingAidsEditHistoryCardErrorText)
  })) : definitions.map(function (item, index) {
    var _item$editor, _item$editor2, _item$confluenceEntit;
    return /*#__PURE__*/React.createElement(Box, {
      xcss: editedDefinitionCardContainerStyles,
      key: "edited-definition-history-card-".concat(index)
    }, /*#__PURE__*/React.createElement(ScopeDescription, {
      scope: item.scope
    }), /*#__PURE__*/React.createElement(Box, {
      xcss: editedDefinitionCardStyles
    }, /*#__PURE__*/React.createElement(Box, {
      xcss: editedDefinitionAvatarStyles
    }, /*#__PURE__*/React.createElement("img", {
      src: ((_item$editor = item.editor) === null || _item$editor === void 0 ? void 0 : _item$editor.picture) || '',
      width: "32px",
      height: "32px"
    })), /*#__PURE__*/React.createElement(Box, {
      xcss: editedDefinitionCardBodyStyles
    }, /*#__PURE__*/React.createElement(Box, {
      xcss: editedDefinitionCardHeaderWrapperStyles
    }, /*#__PURE__*/React.createElement(Inline, {
      xcss: editedDefinitionCardHeaderStyles
    }, formatMessage(messages.readingAidsEditHistoryCardEdited, {
      editorName: /*#__PURE__*/React.createElement(Inline, {
        xcss: editedDefinitionCardEditorStyles
      }, ((_item$editor2 = item.editor) === null || _item$editor2 === void 0 ? void 0 : _item$editor2.name) || '')
    }), ' ∙ ', /*#__PURE__*/React.createElement(TimeAgo, {
      date: Number(item.createdAt) * 1000
    })), /*#__PURE__*/React.createElement("hr", {
      role: "presentation",
      style: editedDefinitionCardHeaderSpacerStyles
    })), /*#__PURE__*/React.createElement("span", {
      "data-testid": "rendered-markdown-definition-".concat(index)
    }, /*#__PURE__*/React.createElement(Suspense, {
      fallback: /*#__PURE__*/React.createElement(Fragment, null)
    }, /*#__PURE__*/React.createElement(MarkdownRenderer, {
      disallowedElements: ['img']
    }, item.definition))), item.referenceUrl && ((_item$confluenceEntit = item.confluenceEntity) === null || _item$confluenceEntit === void 0 ? void 0 : _item$confluenceEntit.title) && /*#__PURE__*/React.createElement(Box, {
      xcss: editedDefinitionCardReferenceStyles
    }, /*#__PURE__*/React.createElement(Link, {
      appearance: "subtle",
      target: "_blank",
      href: item.referenceUrl
    }, item.confluenceEntity.type === 'BLOG_POST' ?
    /*#__PURE__*/
    // TODO : https://product-fabric.atlassian.net/browse/DSP-20772
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    React.createElement(Blog16Icon, {
      label: ""
    }) :
    /*#__PURE__*/
    // TODO : https://product-fabric.atlassian.net/browse/DSP-20772
    // eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
    React.createElement(Page16Icon, {
      label: ""
    }), /*#__PURE__*/React.createElement(Inline, {
      xcss: editedDefinitionCardReferenceTitleStyles
    }, item.confluenceEntity.title || ''))))));
  }))), /*#__PURE__*/React.createElement(ModalFooter, null));
};